/**
 * Fair Home V2 — Search drawer (H13 → H14 to spec)
 * Figma RedisignHomePage `50:10142` desktop (720px) · `50:10480` mobile (393 full-screen)
 * (supersedes `15:9845` / `15:9987`)
 * States 1 Idle · 2 Typing text · 3 Photo+empty · 4 Photo+text · 5 Empty
 * · 6 Photo select detected · 7 Photo select none
 * ≠ Track S PLP — preview overlay only
 */
import type { RouteLocationRaw } from 'vue-router'
import { resolveAudience } from '@/helpers/audience'
import type { MenuRoot } from '@/types/menuItemTypes'
import type { FairProductV2RecsCard } from '@/widgets/product/fair-v2/fair-product-v2.data'
import { buildFairHomeSearchLocation } from '@/widgets/home/fair/fair-home-search'
import type { FairHomeImageSearchPart } from '@/widgets/home/fair/fair-home-image-search.data'
import { mapFairSearchV2TeaserToGridCard } from '@/widgets/search/fair-v2/fair-search-v2-recs.data'
import type {
  ProductTeaser,
  SearchContext,
  SearchV1Request,
  SearchV1Response,
} from '@/features/search/api/searchV1'
import type { RecentSearch } from '@/types/searchItemsTypes'

/** Figma `50:10142` — desktop drawer width (States 1, 6, 7). */
export const FAIR_HOME_V2_SEARCH_DRAWER_WIDTH_DESKTOP = 720
/**
 * Figma `50:10145` States 2–5 — Content row: Preview 372 + gap 32 + divider 1
 * + gap 32 + Drawer content/Search 656, plus Drawer/Content padding 16+16.
 * Idle 720 → results, animated.
 */
export const FAIR_HOME_V2_SEARCH_DRAWER_WIDTH_RESULTS = 1125
/** Figma annotation State 2: "8 cards". */
export const FAIR_HOME_V2_SEARCH_PREVIEW_LIMIT = 8
export const FAIR_HOME_V2_SEARCH_SUGGESTIONS_LIMIT = 6
export const FAIR_HOME_V2_SEARCH_RECENT_MAX = 12
/** Legacy localStorage store (pre-server history) — purged on mount. */
export const FAIR_HOME_V2_SEARCH_RECENT_STORAGE_KEY = 'fair-home-v2-search-recent'
/**
 * Photo tag sidecar: `/search-history` carries only `text` + `image`, the
 * "Jacket / Whole outfit" label the row shows is known only to the browser
 * that picked it. Keyed by the image's basename (see `recentImageBasename`).
 */
export const FAIR_HOME_V2_SEARCH_RECENT_TAGS_STORAGE_KEY = 'fair-home-v2-search-recent-tags'
/** Live preview / suggestions debounce (keystroke → request). */
export const FAIR_HOME_V2_SEARCH_LIVE_DEBOUNCE_MS = 250

export const FAIR_HOME_V2_SEARCH_WHOLE_OUTFIT_ID = 'whole-outfit'

/** State 3 «Add detail» example chips — i18n keys (Figma EN canon in en.json). */
export const FAIR_HOME_V2_SEARCH_ADD_DETAIL_CHIP_KEYS = [
  'fair_home_v2.search.detail_chips.in_black',
  'fair_home_v2.search.detail_chips.summer_wedding',
  'fair_home_v2.search.detail_chips.under_300',
  'fair_home_v2.search.detail_chips.in_cotton',
] as const

export type FairHomeV2SearchDrawerMode =
  | 'idle'
  | 'typing'
  | 'empty'
  | 'photo-detail'
  | 'photo-typing'

export type FairHomeV2SearchDrawerView = 'search' | 'photo-select'

export interface FairHomeV2SearchPhotoRect {
  x: number
  y: number
  width: number
  height: number
}

export interface FairHomeV2SearchPhotoPart {
  id: string
  label: string
  /** Fraction-of-photo rect; omitted → full frame (Whole outfit) */
  rect?: FairHomeV2SearchPhotoRect
}

export interface FairHomeV2SearchRecentItem {
  id: string
  text: string
  /** Photo thumb URL (server URL after upload) */
  photoUrl?: string
  /** Server image ref for search context / PLP `?image=` */
  photoKey?: string
  /** Tag label when photo attached (e.g. Whole outfit / Jacket) */
  photoTag?: string
}

export interface FairHomeV2SearchSuggestion {
  id: string
  label: string
}

export type FairHomeV2SearchPreviewCard = FairProductV2RecsCard

export interface FairHomeV2SearchBrowseCategory {
  id: string
  label: string
}

/* ---------------------------------------------------------------------- */
/* State machine                                                           */
/* ---------------------------------------------------------------------- */

/**
 * Figma annotations:
 * - State 3 photo + empty text · State 1 nothing
 * - State 5 "live — as soon as the current query yields 0 matches"; while a
 *   request is in flight we stay in the typing state (no flash of empty).
 */
export function resolveFairHomeV2SearchDrawerMode(input: {
  query: string
  resultCount: number | null
  hasPhoto?: boolean
}): FairHomeV2SearchDrawerMode {
  const text = input.query.trim()
  const hasPhoto = Boolean(input.hasPhoto)

  if (hasPhoto && !text) return 'photo-detail'
  if (!text) return 'idle'
  if (input.resultCount !== null && input.resultCount <= 0) return 'empty'
  if (hasPhoto) return 'photo-typing'
  return 'typing'
}

/* ---------------------------------------------------------------------- */
/* Live preview — POST /api/v1/search (same contract as Track S PLP)       */
/* ---------------------------------------------------------------------- */

export function buildFairHomeV2SearchContext(
  query: string,
  imageId?: string | null,
): SearchContext | null {
  const text = query.trim()
  const image = imageId?.trim() || null
  if (!text && !image) return null
  if (text && image) return { type: 'text_image', query: text, image_id: image }
  if (image) return { type: 'image', image_id: image }
  return { type: 'text', query: text }
}

/**
 * Preview = first 8 of the same search the Results Page would run.
 * Audience goes through `filters` (not context) — see `buildFairSearchV2Filters`.
 */
export function buildFairHomeV2SearchPreviewRequest(input: {
  query: string
  imageId?: string | null
  audience: string
}): SearchV1Request | null {
  const context = buildFairHomeV2SearchContext(input.query, input.imageId)
  if (!context) return null
  return {
    context,
    filters: { audience: [resolveAudience(input.audience)] },
    limit: FAIR_HOME_V2_SEARCH_PREVIEW_LIMIT,
  }
}

export function mapFairHomeV2SearchPreviewCards(
  response: SearchV1Response | null | undefined,
): FairHomeV2SearchPreviewCard[] {
  const items = (response?.results?.items ?? []) as ProductTeaser[]
  return items
    .slice(0, FAIR_HOME_V2_SEARCH_PREVIEW_LIMIT)
    .map((teaser) => mapFairSearchV2TeaserToGridCard(teaser))
}

export function resolveFairHomeV2SearchPreviewTotal(
  response: SearchV1Response | null | undefined,
): number {
  const total = response?.results?.total
  return typeof total === 'number' && Number.isFinite(total) ? total : 0
}

/* ---------------------------------------------------------------------- */
/* Suggestions — /shopping-list/autocomplete                               */
/* ---------------------------------------------------------------------- */

export function mapFairHomeV2SearchSuggestions(
  suggestions: string[] | null | undefined,
  query: string,
): FairHomeV2SearchSuggestion[] {
  const q = query.trim().toLowerCase()
  if (!q) return []
  const seen = new Set<string>()
  const out: FairHomeV2SearchSuggestion[] = []
  for (const raw of suggestions ?? []) {
    const label = (raw ?? '').trim()
    if (!label) continue
    const key = label.toLowerCase()
    if (seen.has(key) || key === q) continue
    seen.add(key)
    out.push({ id: label, label })
    if (out.length >= FAIR_HOME_V2_SEARCH_SUGGESTIONS_LIMIT) break
  }
  return out
}

/** Bold the matched substring for suggestion rows */
export function splitFairHomeV2SearchSuggestionMatch(
  label: string,
  query: string,
): { before: string; match: string; after: string } {
  const q = query.trim()
  if (!q) return { before: label, match: '', after: '' }
  const index = label.toLowerCase().indexOf(q.toLowerCase())
  if (index < 0) return { before: label, match: '', after: '' }
  return {
    before: label.slice(0, index),
    match: label.slice(index, index + q.length),
    after: label.slice(index + q.length),
  }
}

/* ---------------------------------------------------------------------- */
/* State 5 — "Or browse:" top-level categories                             */
/* ---------------------------------------------------------------------- */

/**
 * Figma: "links to top-level categories — sourced from the real category
 * tree, filtered to the gender currently active in the site header".
 * `/shopping-list/category-tree` has no gender axis today, so the roots are
 * the same for every tab; gender scopes the Results Page via `?audience=`.
 */
export function resolveFairHomeV2SearchBrowseCategories(
  tree: MenuRoot | null | undefined,
): FairHomeV2SearchBrowseCategory[] {
  return (tree ?? []).map((root) => ({ id: root.id, label: root.label }))
}

export function buildFairHomeV2SearchBrowseLocation(
  category: FairHomeV2SearchBrowseCategory,
  audience: string,
): RouteLocationRaw {
  return {
    path: `/searchList/${category.label}`,
    query: { audience: resolveAudience(audience), category: category.id },
  }
}

/* ---------------------------------------------------------------------- */
/* Photo — upload + get-image-parts → fraction rects                        */
/* ---------------------------------------------------------------------- */

/**
 * `get-image-parts` returns image-space pixels without ids; the frame wants
 * fractions of the rendered photo. Labels may be missing — caller supplies a
 * fallback per index (i18n).
 */
export function mapFairHomeV2SearchPhotoParts(
  parts: FairHomeImageSearchPart[],
  natural: { width: number; height: number },
  fallbackLabel: (index: number) => string,
): FairHomeV2SearchPhotoPart[] {
  const w = natural.width
  const h = natural.height
  if (!(w > 0) || !(h > 0)) return []
  const clamp = (value: number) => Math.min(1, Math.max(0, value))
  return parts.map((part, index) => ({
    id: `part-${index}`,
    label: (part.label ?? '').trim() || fallbackLabel(index),
    rect: {
      x: clamp(part.x / w),
      y: clamp(part.y / h),
      width: clamp(part.width / w),
      height: clamp(part.height / h),
    },
  }))
}

export function resolveFairHomeV2SearchPhotoFrame(
  parts: FairHomeV2SearchPhotoPart[],
  selectedId: string,
): FairHomeV2SearchPhotoRect {
  if (selectedId === FAIR_HOME_V2_SEARCH_WHOLE_OUTFIT_ID) {
    return { x: 0.04, y: 0.04, width: 0.92, height: 0.92 }
  }
  const part = parts.find((item) => item.id === selectedId)
  if (part?.rect) return part.rect
  return { x: 0.04, y: 0.04, width: 0.92, height: 0.92 }
}

export function loadFairHomeV2SearchImageSize(
  src: string,
): Promise<{ width: number; height: number }> {
  return new Promise((resolve, reject) => {
    const img = new Image()
    img.onload = () => resolve({ width: img.naturalWidth, height: img.naturalHeight })
    img.onerror = () => reject(new Error('image load failed'))
    img.src = src
  })
}

/* ---------------------------------------------------------------------- */
/* Recent                                                                  */
/* ---------------------------------------------------------------------- */

/**
 * Server history hands an image back as a presigned S3 URL, the drawer sent it
 * as the upload key. Both end in the same object name, so that is the join.
 */
export function recentImageBasename(image: string | null | undefined): string {
  const raw = (image ?? '').trim()
  if (!raw) return ''
  const withoutQuery = raw.split('?')[0] ?? ''
  const segments = withoutQuery.split('/').filter(Boolean)
  return segments[segments.length - 1] ?? ''
}

export type FairHomeV2SearchRecentTagMap = Record<string, string>

/**
 * `GET /search-history/get_history` → drawer rows.
 * `image` is presigned (1h): usable as thumb, and as `image_id` for a re-run
 * when the original key was not kept — the API resolves either.
 */
export function mapFairHomeV2SearchRecentFromServer(
  recent: RecentSearch[] | null | undefined,
  tags: FairHomeV2SearchRecentTagMap,
  fallbackPhotoTag: string,
): FairHomeV2SearchRecentItem[] {
  const items: FairHomeV2SearchRecentItem[] = []
  for (const entry of recent ?? []) {
    if (!entry || typeof entry.id !== 'string') continue
    const text = (entry.query?.text ?? '').trim()
    const image = typeof entry.query?.image === 'string' ? entry.query.image.trim() : ''
    if (!text && !image) continue
    const item: FairHomeV2SearchRecentItem = { id: entry.id, text }
    if (image) {
      item.photoUrl = image
      item.photoKey = image
      item.photoTag = tags[recentImageBasename(image)] || fallbackPhotoTag
    }
    items.push(item)
  }
  return items.slice(0, FAIR_HOME_V2_SEARCH_RECENT_MAX)
}

/**
 * Figma State 1 label logic: text → text; text + photo → text; photo only →
 * selected tag from photo-select (Jacket / Whole outfit / Custom area).
 */
export function recentItemLabel(item: FairHomeV2SearchRecentItem): string {
  if (item.photoTag && !item.text) return item.photoTag
  return item.text || item.photoTag || ''
}

export function dedupeFairHomeV2SearchRecent(
  items: FairHomeV2SearchRecentItem[],
  next: FairHomeV2SearchRecentItem,
): FairHomeV2SearchRecentItem[] {
  const key = recentDedupeKey(next)
  const filtered = items.filter((item) => recentDedupeKey(item) !== key)
  return [next, ...filtered].slice(0, FAIR_HOME_V2_SEARCH_RECENT_MAX)
}

function recentDedupeKey(item: FairHomeV2SearchRecentItem): string {
  const text = item.text.trim().toLowerCase()
  const tag = (item.photoTag ?? '').trim().toLowerCase()
  return `${text}::${tag}`
}

/* ---------------------------------------------------------------------- */
/* Submit → Results Page                                                   */
/* ---------------------------------------------------------------------- */

export function buildFairHomeV2SearchLocation(
  query: string,
  imageKey?: string | null,
  audience?: string,
) {
  // Local blob previews never leave the browser — only the uploaded key goes to PLP.
  const safeImage = imageKey && !imageKey.startsWith('blob:') ? imageKey : undefined
  return buildFairHomeSearchLocation({
    query,
    image: safeImage,
    existingQuery: audience ? { audience: resolveAudience(audience) } : undefined,
  })
}
